<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import BaseBubble from './Base.vue';
import Icon from 'next/icon/Icon.vue';
import { useMessageContext } from '../provider.js';

const { content, contentAttributes } = useMessageContext();
const { t } = useI18n();

const expandedSections = ref({});

const listData = computed(() => {
  const attrs = contentAttributes.value || {};

  return {
    header: attrs.header || attrs.title || '',
    body: attrs.body || content.value || '',
    footer: attrs.footer || '',
    buttonText:
      attrs.buttonText ||
      attrs.button_text ||
      t('CONVERSATION.INTERACTIVE.VIEW_OPTIONS'),
    sections: attrs.sections || attrs.list || [],
  };
});

const hasHeader = computed(() => !!listData.value.header);
const hasFooter = computed(() => !!listData.value.footer);
const hasSections = computed(() => listData.value.sections.length > 0);

function toggleSection(index) {
  expandedSections.value[index] = !expandedSections.value[index];
}

function isSectionExpanded(index) {
  return expandedSections.value[index] ?? true;
}

function getSectionTitle(section) {
  if (typeof section === 'string') return section;
  return section.title || section.name || '';
}

function getSectionItems(section) {
  if (!section || typeof section === 'string') return [];
  return section.rows || section.items || section.options || [];
}

function getItemTitle(item) {
  if (typeof item === 'string') return item;
  return item.title || item.text || item.name || '';
}

function getItemDescription(item) {
  if (typeof item === 'string') return '';
  return item.description || item.subtitle || '';
}
</script>

<template>
  <BaseBubble
    class="overflow-hidden p-3 min-w-48 max-w-sm"
    data-bubble-name="interactive-list"
  >
    <div class="space-y-3">
      <!-- Header -->
      <div v-if="hasHeader" class="text-sm font-semibold text-n-slate-12">
        {{ listData.header }}
      </div>

      <!-- Body -->
      <div
        v-if="listData.body"
        class="text-sm text-n-slate-12 whitespace-pre-wrap"
      >
        {{ listData.body }}
      </div>

      <!-- Footer -->
      <div v-if="hasFooter" class="text-xs text-n-slate-11">
        {{ listData.footer }}
      </div>

      <!-- List Button Preview -->
      <div v-if="hasSections" class="pt-2 border-t border-n-weak">
        <div
          class="w-full px-4 py-2 rounded-lg text-sm text-center bg-n-alpha-2 text-woot-600 border border-n-weak flex items-center justify-center gap-2 cursor-default"
        >
          <Icon icon="i-ph-list" class="size-4" />
          <span>{{ listData.buttonText }}</span>
        </div>
      </div>

      <!-- Expandable Sections -->
      <div v-if="hasSections" class="space-y-2 pt-2">
        <div
          v-for="(section, sectionIndex) in listData.sections"
          :key="sectionIndex"
          class="border border-n-weak rounded-lg overflow-hidden"
        >
          <!-- Section Header -->
          <button
            class="w-full px-3 py-2 flex items-center justify-between bg-n-alpha-1 hover:bg-n-alpha-2 transition-colors text-sm font-medium text-n-slate-12"
            @click="toggleSection(sectionIndex)"
          >
            <span>{{ getSectionTitle(section) }}</span>
            <Icon
              icon="i-ph-caret-down"
              class="size-4 text-n-slate-11 transition-transform"
              :class="{ 'rotate-180': isSectionExpanded(sectionIndex) }"
            />
          </button>

          <!-- Section Items -->
          <div
            v-if="isSectionExpanded(sectionIndex)"
            class="divide-y divide-n-weak"
          >
            <div
              v-for="(item, itemIndex) in getSectionItems(section)"
              :key="itemIndex"
              class="px-3 py-2 bg-n-solid-1"
            >
              <div class="text-sm text-n-slate-12">
                {{ getItemTitle(item) }}
              </div>
              <div
                v-if="getItemDescription(item)"
                class="text-xs text-n-slate-11 mt-0.5"
              >
                {{ getItemDescription(item) }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Read-only indicator -->
      <div
        v-if="hasSections"
        class="text-xs text-n-slate-10 text-center italic"
      >
        {{ t('CONVERSATION.INTERACTIVE.LIST_READ_ONLY') }}
      </div>
    </div>
  </BaseBubble>
</template>
